import React from 'react';
import { Badge } from '@/components/ui/badge';
import { isCurrentWeek } from './planningApi';
import { chapterColor, chapterNumber, termGradient } from './planningColors';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const HOLIDAY_RE = /holiday|férié|congé|no school|good friday|easter|ascension|assumption|labour day|custom chiefs|children'?s day|independence|constitution|unity day|father lini|christmas|boxing day|family day|anzac|new year|lenten|all saints/i;

export const PlannerYearTable = ({ weeks, today, onOpenWeek }) => {
  const terms = [...new Set(weeks.map((w) => w.term))];
  const chapters = [...new Map(weeks.flatMap((w) => String(w.chapter || '').split(/[;\n]/).map((c) => c.trim()).filter(Boolean))
    .map((c) => [chapterNumber(c) || c, c])).values()].sort((a, b) => (chapterNumber(a) || 99) - (chapterNumber(b) || 99));
  return (
    <div className="space-y-6" data-testid="planner-year-table">
      <div className="flex flex-wrap gap-2" data-testid="planner-chapter-legend">
        {chapters.map((c) => {
          const col = chapterColor(c);
          return <span key={c} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${col.bg} ${col.text} ${col.border}`}><span className={`h-2 w-2 rounded-full ${col.dot}`} />{c}</span>;
        })}
        <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs text-rose-800"><span className="h-2 w-2 rounded-full bg-rose-500" />Public holiday</span>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-xs text-emerald-800"><span className="h-2 w-2 rounded-full bg-emerald-500" />This week</span>
      </div>
      {terms.map((term) => (
        <div key={term} className="rounded-2xl border overflow-hidden shadow-sm">
          <div className={`bg-gradient-to-r ${termGradient(term)} text-white px-5 py-3 flex items-center justify-between`}>
            <span className="font-semibold text-sm uppercase tracking-wider">{term}</span>
            <span className="text-xs text-white/80">{weeks.filter((w) => w.term === term).length} weeks</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left w-12">Wk</th>
                  {DAYS.map((d) => <th key={d} className="px-2 py-2 text-left w-24">{d}</th>)}
                  <th className="px-3 py-2 text-left">Chapter / focus</th>
                  <th className="px-3 py-2 text-left">Assessment</th>
                  <th className="px-3 py-2 text-left w-16">Lessons</th>
                  <th className="px-3 py-2 text-left">Notes</th>
                </tr>
              </thead>
              <tbody>
                {weeks.filter((w) => w.term === term).map((w) => {
                  const now = isCurrentWeek(w, today);
                  const col = chapterColor(w.chapter);
                  return (
                    <tr key={w.id} onClick={() => onOpenWeek?.(w)} data-testid={`planner-week-${w.term.replace(/\s/g, '')}-${w.week}`}
                      className={`border-t cursor-pointer transition-colors hover:brightness-95 ${col.bg} ${now ? 'ring-2 ring-inset ring-emerald-500' : ''}`}>
                      <td className="px-3 py-2 font-semibold text-slate-800">
                        <span className="inline-flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-full ${col.dot}`} />{w.week}</span>
                        {now && <Badge className="ml-1 bg-emerald-600 text-[10px] px-1.5">Now</Badge>}
                      </td>
                      {DAYS.map((d, i) => {
                        const cell = w.days?.[i] || '';
                        const [first, ...rest] = cell.split('\n');
                        const note = rest.join(' ').trim();
                        const holiday = HOLIDAY_RE.test(note);
                        return (
                          <td key={d} className={`px-2 py-2 text-xs align-top ${holiday ? 'bg-rose-100 text-rose-800 font-medium rounded' : 'text-slate-600'}`}>
                            <div className={holiday ? '' : 'text-slate-400 font-medium'}>{first}</div>
                            {note && <div className={`mt-0.5 leading-snug ${holiday ? '' : 'text-slate-700'}`} title={note}>{note}</div>}
                          </td>
                        );
                      })}
                      <td className={`px-3 py-2 font-medium whitespace-pre-line ${col.text}`}>{w.chapter}</td>
                      <td className="px-3 py-2">{w.assessment && <Badge className="bg-indigo-600 hover:bg-indigo-600 text-white whitespace-normal shadow-sm">{w.assessment}</Badge>}</td>
                      <td className="px-3 py-2 text-center text-slate-700">{w.lessons ?? ''}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{w.notes}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};
